import React, { useState, useEffect } from 'react';
import { veoCreditsService, VeoCreditTransaction } from '../../services/veoCreditsService';
import styles from './VeoCreditHistoryModal.module.css';

interface VeoCreditHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

const VeoCreditHistoryModal: React.FC<VeoCreditHistoryModalProps> = ({
  isOpen,
  onClose,
  userId,
}) => {
  const [transactions, setTransactions] = useState<VeoCreditTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadTransactions();
    }
  }, [isOpen]);

  const loadTransactions = async () => {
    setLoading(true);
    setError('');

    try {
      const history = await veoCreditsService.getCreditHistory(50, 0);
      setTransactions(history);
    } catch (err) {
      console.error('Error loading transaction history:', err);
      setError('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionIcon = (type: string): string => {
    switch (type) {
      case 'purchase': return '💳';
      case 'consumption': return '🎬';
      case 'refund': return '↩️';
      case 'admin_adjustment': return '⚙️';
      default: return '📊';
    }
  };

  const getTransactionColor = (type: string): string => {
    switch (type) {
      case 'purchase': return '#10b981';
      case 'consumption': return '#00d4ff';
      case 'refund': return '#f59e0b';
      case 'admin_adjustment': return '#8b5cf6';
      default: return '#64748b';
    }
  };

  const getTransactionSign = (credits: number): string => {
    return credits > 0 ? '+' : '';
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title}>VEO TRANSACTION LOG</h2>
            <p className={styles.subtitle}>
              Complete history of your VEO credit transactions
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <span>×</span>
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <span className={styles.loadingText}>Accessing transaction records...</span>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <span className={styles.errorIcon}>⚠️</span>
              <span className={styles.errorText}>{error}</span>
              <button className={styles.retryButton} onClick={loadTransactions}>
                Retry
              </button>
            </div>
          ) : transactions.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📊</div>
              <h3 className={styles.emptyTitle}>No Transactions Found</h3>
              <p className={styles.emptyText}>
                Your VEO credit transaction history will appear here once you start using the system.
              </p>
            </div>
          ) : (
            <div className={styles.transactionsList}>
              {transactions.map((transaction) => (
                <div key={transaction.id} className={styles.transactionItem}>
                  <div className={styles.transactionLeft}>
                    <div 
                      className={styles.transactionIcon}
                      style={{ backgroundColor: getTransactionColor(transaction.type) }}
                    >
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div className={styles.transactionInfo}>
                      <div className={styles.transactionDescription}>
                        {transaction.description || `${transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)} Transaction`}
                      </div>
                      <div className={styles.transactionMeta}>
                        <span className={styles.transactionDate}>
                          {formatDate(transaction.createdAt)}
                        </span>
                        {transaction.veoModel && (
                          <span className={styles.veoModel}>
                            • {transaction.veoModel.replace('-generate-preview', '').replace('-', ' ').toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.transactionRight}>
                    <div 
                      className={styles.creditsChange}
                      style={{ color: getTransactionColor(transaction.type) }}
                    >
                      {getTransactionSign(transaction.credits)}{Math.abs(transaction.credits)}
                    </div>
                    {transaction.amount && (
                      <div className={styles.amount}>
                        ${transaction.amount.toFixed(2)}
                      </div>
                    )}
                    <div className={styles.balanceAfter}>
                      Balance: {transaction.balanceAfter}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            <span className={styles.infoIcon}>ℹ️</span>
            <span>Transaction records are kept for audit and billing purposes</span>
          </div>
          <button className={styles.closeButtonFooter} onClick={onClose}>
            Close
          </button>
        </div>

        {/* Futuristic Effects */}
        <div className={styles.modalEffects}>
          <div className={styles.scanLine}></div>
          <div className={styles.dataGrid}></div>
        </div>
      </div>
    </div>
  );
};

export default VeoCreditHistoryModal;
